import React from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface DateTimeConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const DateTimeConfig: React.FC<DateTimeConfigProps> = ({ node, updateConfig }) => {
  return (
    <div className="space-y-4">
      <ConfigSection title="Dátum/Idő művelet">
        <ConfigField
          label="Művelet típusa"
          description="Mit szeretnél csinálni a dátummal"
        >
          <select
            value={node.data.config?.operation || 'now'}
            onChange={(e) => updateConfig('operation', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="now">Jelenlegi időpont</option>
            <option value="format">Formázás</option>
            <option value="parse">Szövegből dátum</option>
            <option value="add">Hozzáadás</option>
            <option value="subtract">Kivonás</option>
            <option value="diff">Különbség számítás</option>
            <option value="compare">Összehasonlítás</option>
          </select>
        </ConfigField>

        {node.data.config?.operation !== 'now' && (
          <ConfigField
            label="Bemeneti dátum"
            description="A feldolgozandó dátum (változó vagy ISO string)"
          >
            <input
              type="text"
              value={node.data.config?.inputDate || ''}
              onChange={(e) => updateConfig('inputDate', e.target.value)}
              placeholder="{{date}} vagy 2024-01-01"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </ConfigField>
        )}

        {(node.data.config?.operation === 'format' || node.data.config?.operation === 'now') && (
          <ConfigField
            label="Formátum"
            description="Kimeneti formátum"
          >
            <select
              value={node.data.config?.format || 'ISO'}
              onChange={(e) => updateConfig('format', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="ISO">ISO 8601 (2024-01-15T10:30:00Z)</option>
              <option value="date">Dátum (2024-01-15)</option>
              <option value="time">Idő (10:30:00)</option>
              <option value="datetime">Dátum és idő (2024-01-15 10:30:00)</option>
              <option value="timestamp">Unix timestamp (1705315800)</option>
              <option value="hu">Magyar (2024. január 15. 10:30)</option>
              <option value="custom">Egyéni formátum</option>
            </select>
          </ConfigField>
        )}

        {node.data.config?.format === 'custom' && (
          <ConfigField
            label="Egyéni formátum"
            description="Formátum string (pl. YYYY-MM-DD HH:mm:ss)"
          >
            <input
              type="text"
              value={node.data.config?.customFormat || ''}
              onChange={(e) => updateConfig('customFormat', e.target.value)}
              placeholder="YYYY-MM-DD HH:mm:ss"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </ConfigField>
        )}

        {(node.data.config?.operation === 'add' || node.data.config?.operation === 'subtract') && (
          <>
            <ConfigField
              label="Érték"
              description="Mennyit adj hozzá/vonj ki"
            >
              <input
                type="number"
                value={node.data.config?.value || 1}
                onChange={(e) => updateConfig('value', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="Egység"
              description="Az érték egysége"
            >
              <select
                value={node.data.config?.unit || 'days'}
                onChange={(e) => updateConfig('unit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="seconds">Másodperc</option>
                <option value="minutes">Perc</option>
                <option value="hours">Óra</option>
                <option value="days">Nap</option>
                <option value="weeks">Hét</option>
                <option value="months">Hónap</option>
                <option value="years">Év</option>
              </select>
            </ConfigField>
          </>
        )}

        {node.data.config?.operation === 'diff' && (
          <>
            <ConfigField
              label="Második dátum"
              description="Amivel összehasonlítod"
            >
              <input
                type="text"
                value={node.data.config?.compareDate || ''}
                onChange={(e) => updateConfig('compareDate', e.target.value)}
                placeholder="{{date2}} vagy 2024-12-31"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="Eredmény egység"
              description="Milyen egységben add vissza a különbséget"
            >
              <select
                value={node.data.config?.diffUnit || 'days'}
                onChange={(e) => updateConfig('diffUnit', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="seconds">Másodperc</option>
                <option value="minutes">Perc</option>
                <option value="hours">Óra</option>
                <option value="days">Nap</option>
              </select>
            </ConfigField>
          </>
        )}

        <ConfigField
          label="Időzóna"
          description="Időzóna beállítás"
        >
          <select
            value={node.data.config?.timezone || 'Europe/Budapest'}
            onChange={(e) => updateConfig('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="Europe/Budapest">Budapest (CET/CEST)</option>
            <option value="UTC">UTC</option>
            <option value="Europe/London">London (GMT/BST)</option>
            <option value="America/New_York">New York (EST/EDT)</option>
            <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
          </select>
        </ConfigField>
      </ConfigSection>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>📅 Példák:</strong>
          <br />• <strong>now:</strong> Jelenlegi időpont lekérése
          <br />• <strong>add:</strong> Mai nap + 7 nap = jövő hét
          <br />• <strong>diff:</strong> Két dátum közötti napok száma
          <br />• <strong>format:</strong> ISO → Magyar formátum
        </div>
      </div>
    </div>
  );
};

export default DateTimeConfig;
